import React, { useEffect, useState } from 'react';
import { Settings, RefreshCw } from 'lucide-react';
import { apiProxyFetch } from '../../lib/api';

interface ScaffoldPanelProps {
  /** Panel title */
  title: string;
  /** Column headers shown when no data is loaded */
  columns: string[];
  /** Resolved API endpoint URL (from settings) */
  endpointUrl?: string;
  /** Settings key path for documentation (e.g. "officeApps.kaalvoet.projectsApiUrl") */
  endpointKey?: string;
}

/**
 * ScaffoldPanel — data panel for office-specific apps.
 *
 * Behaviour:
 * - If `endpointUrl` is set → fetch JSON from that URL via backend proxy and display rows
 * - If not set → show "Configure endpoint in Settings" placeholder with animated skeleton rows
 *
 * Data format accepted from the endpoint (any of):
 *   - Array:           [{ col: val, ... }, ...]
 *   - { rows: [...] }
 *   - { data: [...] }
 *   - Any other object: wrapped in a single-row table
 *
 * Extension by KAELO:
 *   1. Set the endpoint URL in Settings → Office App APIs → [Office] → [App] URL
 *   2. Return JSON from that endpoint in any of the formats above
 *   3. Column headers are auto-detected from the first row's keys
 */
export default function ScaffoldPanel({ title, columns, endpointUrl, endpointKey }: ScaffoldPanelProps) {
  const [rows, setRows] = useState<Record<string, any>[] | null>(null);
  const [headers, setHeaders] = useState<string[]>(columns);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (url: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiProxyFetch(url);
      const extracted = extractRows(data);
      if (extracted.length > 0) {
        setHeaders(Object.keys(extracted[0]));
        setRows(extracted);
      } else {
        setRows([]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (endpointUrl) fetchData(endpointUrl);
    else { setRows(null); setHeaders(columns); }
  }, [endpointUrl]);

  const isConfigured = !!endpointUrl;

  return (
    <div
      className="flex flex-col h-full"
      data-testid={`scaffold-panel-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {!isConfigured && (
            <p className="text-xs text-gray-400 mt-0.5">Configure endpoint in Settings to load data</p>
          )}
          {isConfigured && rows !== null && (
            <p className="text-xs text-gray-400 mt-0.5">{rows.length} rows</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isConfigured && (
            <button
              onClick={() => fetchData(endpointUrl!)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              data-testid="scaffold-refresh-button"
            >
              <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          )}
          {!isConfigured && (
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200">
              <Settings size={12} />
              <span>Not configured</span>
            </div>
          )}
          {isConfigured && error && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-1.5 rounded-lg border border-red-200">
              <span>Error loading</span>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-600">{error}</p>
            <p className="text-[10px] text-red-400 mt-1 font-mono truncate">{endpointUrl}</p>
          </div>
        )}

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {headers.map((col) => (
                <th
                  key={col}
                  className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Real data rows */}
            {rows && rows.length > 0 && rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                {headers.map((col) => (
                  <td key={col} className="py-2.5 pr-4 text-sm text-gray-700">
                    {renderCell(row[col])}
                  </td>
                ))}
              </tr>
            ))}

            {/* Empty state */}
            {rows && rows.length === 0 && (
              <tr>
                <td colSpan={headers.length} className="py-10 text-center text-sm text-gray-400">
                  No data returned from endpoint
                </td>
              </tr>
            )}

            {/* Placeholder skeleton rows */}
            {!rows && Array.from({ length: 5 }, (_, i) => (
              <tr key={i} className="border-b border-gray-50">
                {headers.map((col, j) => (
                  <td key={col} className="py-3 pr-4">
                    <div
                      className="h-3 rounded bg-gray-100 animate-pulse"
                      style={{ width: j === 0 ? '75%' : `${38 + (i * 13 + j * 7) % 42}%` }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Configuration hint when not configured */}
        {!isConfigured && (
          <div className="mt-5 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-xs font-semibold text-gray-600 mb-1">KAELO Extension Point</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Open Settings → Office App APIs → configure the endpoint URL for this panel.
              {endpointKey && (
                <> Settings key:{' '}
                  <code className="bg-gray-100 px-1 rounded text-[10px] font-mono">{endpointKey}</code>
                </>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function extractRows(data: any): Record<string, any>[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.rows)) return data.rows;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && typeof data === 'object') return [data];
  return [];
}

function renderCell(value: any): React.ReactNode {
  if (value === null || value === undefined) return <span className="text-gray-300">—</span>;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  const str = String(value);
  if (str.length > 60) return str.slice(0, 58) + '…';
  return str;
}
